
import React, { useEffect, useState, useRef } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import DataTable from "react-data-table-component";

//Components
import Layout from "../../../Layout/Layout";
import Constants from "../../../common/SupplierConstants";
import CommonItemLevelDatatableModal from "../../../Components/Modal/supplier_app/CommonItemLevelDatatableModal";
import { SearchField } from "../../../Components/formElements/SearchField";
// eslint-disable-next-line 
import { MessageCircle } from "lucide-react";
import ChatBox from "../../../Components/ChatBox/ChatBox";
import ReverseTimer from "../../../Components/TableReverseTimer/ReverseTimer";
import { formatDateTime } from "../../../common/Validations";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  addauctionNr,
  getEauction,
} from "../../../redux/feature/supplier_app/eauctionSlice";
import StickyColumn from "../../../Components/Sticky/StickyColumn";
import ItemService from "../../../services/customer_appCommonService";
import { TableStatusField } from "../../../Components/formElements/TableStatusField";
import socket from "../../../utils/socket";

//  Main Component 
const BuyerReverseAuctionList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  localStorage.setItem("menu", "eAuction");
  localStorage.setItem("submenu", "Reverse Auction");

  let customerData = useSelector((state) => state.eauctionSlice.eauction);
  let customerTable = useSelector(
    (state) => state.eauctionSlice.eauction.pagination
  );

  const [data, setData] = useState([]);
  const [isCardVisible, setCardVisible] = useState(true);
  const [getNewPerPage, setNewPerPage] = useState(10);
  const [itemLevelModalShow, setItemLevelModalShow] = useState(false);
  const [getCommonSearchTxt, setCommonSearchTxt] = useState("");
  const [loadingId, setLoadingId] = useState(null);
  const [chatId, setChatId] = useState(null);
  // Tracks the latest known closingDateTime per auction (from socket events),
  // so a stale 30s poll response can never regress a row's displayed time
  const latestClosingTimesRef = useRef({});

  const itemLevelHideModal = () => setItemLevelModalShow(false);
  const toggleCardVisibility = () => setCardVisible(!isCardVisible);

useEffect(() => {
    // Filter to show only active auctions. Status (kept fresh by the backend's
    // periodic auto-close sweep) is the source of truth here -- NOT closingDateTime,
    // which can be briefly stale right after an extension until the next poll catches up.
    if (customerData?.data) {
      const activeAuctions = customerData.data.filter((auction) => {
        if (auction.deliveryStatusHeader === "Closed" || auction.auctionStatus === "Ended") {
          return false;
        }
        // Legacy rows with no status set at all: fall back to a time check
        if (auction.deliveryStatusHeader == null && auction.auctionStatus == null && auction.closingDateTime) {
          return new Date(auction.closingDateTime) > new Date();
        }
        return true;
      }).map((auction) => {
        // If we've already learned a later closing time via socket for this auction
        // (e.g. an extension the poll hasn't picked up yet), keep the newer one, for display.
        const known = latestClosingTimesRef.current[auction.auctionNr || auction.documentNumber];
        if (known && (!auction.closingDateTime || new Date(known) > new Date(auction.closingDateTime))) {
          return { ...auction, closingDateTime: known };
        }
        return auction;
      });
      setData(activeAuctions);
    }
  }, [customerData]);

  // Real-time sync: listen for auto-extension / auto-close events so the
  // list updates immediately instead of waiting for the next 30s poll
  useEffect(() => {
    if (!socket.connected) socket.connect();

    const handleExtended = (payload) => {
      if (!payload?.auctionId) return;
      if (payload.newClosingTime) {
        // Remember this so a stale 30s poll response can't overwrite it
        latestClosingTimesRef.current[payload.auctionId] = payload.newClosingTime;
      }
      setData((prev) =>
        prev.map((row) =>
          (row.auctionNr || row.documentNumber) === payload.auctionId
            ? {
                ...row,
                closingDateTime: payload.newClosingTime || row.closingDateTime,
              }
            : row
        )
      );
    };

    const handleEnded = (payload) => {
      if (!payload?.auctionId) return;
      delete latestClosingTimesRef.current[payload.auctionId];
      // Auction just ended -> it belongs in history now, drop it from this active list
      setData((prev) => prev.filter((row) => (row.auctionNr || row.documentNumber) !== payload.auctionId));
    };

    socket.on("auction:extended", handleExtended);
    socket.on("auction:ended", handleEnded);

    return () => {
      socket.off("auction:extended", handleExtended);
      socket.off("auction:ended", handleEnded);
    };
  }, []);

  // Join the socket room for every auction currently visible in the list,
  // so this page receives its extend/end events (rooms are per-auction on the backend)
  useEffect(() => {
    data.forEach((row) => {
      const rowAuctionId = row?.auctionNr || row?.documentNumber;
      if (rowAuctionId) {
        socket.emit("auction:join", { auctionId: rowAuctionId, userRole: "buyer" });
      }
    });
  }, [data]);
  
  const handlePageChange = (page) => {
    let obj = {
      queryName: "getReverseAuctionList",
      module: "",
      page: page,
      per_page: getNewPerPage,
    };
    dispatch(getEauction(obj));
  };

  const handlePerRowsChange = async (newPerPage, page) => {
    let obj = {
      queryName: "getReverseAuctionList",
      module: "",
      page: page,
      per_page: newPerPage,
    };
    setNewPerPage(newPerPage);
    dispatch(getEauction(obj));
  };

  const handleSearchFuncApi = async () => {
    let obj = {
      queryName: "getReverseAuctionList",
      module: "",
      page: 1,
      per_page: 10,
      searchTerm: getCommonSearchTxt,
    };
    dispatch(getEauction(obj));
  };

  useEffect(() => {
    let obj = {
      queryName: "getReverseAuctionList",
      module: "",
      page: 1,
      per_page: 10,
    };
    dispatch(getEauction(obj));

    // Refresh data every 30 seconds to sync timer updates
    const refreshInterval = setInterval(() => {
      dispatch(getEauction(obj));
    }, 30000);

    return () => clearInterval(refreshInterval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //  Live Auction click → direct navigate (no modal) 
  const liveAuction = (auctionRow) => {
    dispatch(addauctionNr(auctionRow?.documentNumber));
    navigate(Constants.EAUCTION_LIVE_AUCTION_DETAILS);
  };

  const detailedCommercialEvaluation = () => {
    navigate(Constants.EAUCTION_BUYERAUCTION_CREATE);
  };

  const auctionDetail = (auctionNr) => {
    dispatch(addauctionNr(auctionNr?.documentNumber));
    navigate(Constants.EAUCTION_BUYERAUCTION_DETAILS);
  };
// eslint-disable-next-line 
  const toggleChat = (groupId) => {
    if (chatId === groupId) {
      setChatId(null);
    } else {
      setChatId(groupId);
    }
  };

  const handleDownloadReport = async (row) => {
    const auctionId = row?.documentNumber || row?.auctionNr;
    if (!auctionId) return alert("Auction ID not found");
    setLoadingId(auctionId);
    try {
      const blob = await ItemService.downloadAuctionReport({ auctionId });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `eAuction_Report_${auctionId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("PDF download failed:", err);
      alert("Failed to download report. Please try again.");
    } finally {
      setLoadingId(null);
    }
  };

  const isAuctionExpired = (row) => {
    // Manually closed (before scheduled time) should count as expired too
    if (row?.deliveryStatusHeader === "Closed" || row?.auctionStatus === "Ended") {
      return true;
    }
    if (!row?.closingDateTime) return true;
    const now = new Date();
    const closeTime = new Date(row.closingDateTime);
    return now >= closeTime;
  };

  const columns = [
   
    {
      name: "Auction Number",
      cell: (row) => (
        <button
          className="btnInfo"
          onClick={() => auctionDetail(row)}
          style={{ cursor: "pointer" }}
        >
          {row.auctionNr || "-"}{" "}
        </button>
      ),
      selector: (row) => row.auctionNr,
      sortable: true,
      width: "150px",
    },
    {
      name: "RFQ Number",
      selector: (row) => row.rfqNr || "--",
      sortable: true,
      style: {
        position: "sticky",
        left: 0,
        background: "#fff",
        zIndex: 2,
      },
      cell: (row) => (
        <button className="btnInfo" onClick={() => auctionDetail(row)}>
          {row.rfqNr || "-"}{" "}
        </button>
      ),
      width: "150px",
    },
    {
      name: "Status",
      selector: (row) => row?.auctionStatus,
      cell: (row) => (
        <>
          <span className="bg-table-status bg-table-new-status">
            {row.auctionStatus}
          </span>
        </>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "Auction Type",
      cell: (row) => (
        <TableStatusField status={row.auctionType} />
      ),
      selector: (row) => row.auctionType || "--",
      sortable: true,
      width: "150px",
    },
     {
      name: "Auction Report",
      cell: (row) => {
        const auctionId = row?.documentNumber || row?.auctionNr;
        const isLoading = loadingId === auctionId;
        return (
          <span className="iconTable">
            <button
              title="Download Auction Report"
              onClick={() => handleDownloadReport(row)}
              disabled={isLoading}
              className="btn btnTable"
              style={{
                background: isLoading ? "#7f8c8d" : "#c0392b",
                color: "#fff",
                border: "none",
                fontWeight: 600,
                fontSize: "12px",
                cursor: isLoading ? "not-allowed" : "pointer",
                minWidth: "110px",
              }}
            >
              {isLoading ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Loading...
                </>
              ) : (
                <>
                  <i className="bi bi-file-earmark-pdf me-1"></i>
                  Report
                </>
              )}
            </button>
          </span>
        );
      },
      sortable: true,
      width: "140px",
    },
    {
      name: "Start Time",
      selector: (row) => formatDateTime(row.openingDateTime, "DD-MM-YYYY HH:mm:ss") || "--",
      sortable: true,
      width: "160px",
    },
    {
      name: "End Time",
      selector: (row) => formatDateTime(row.closingDateTime, "DD-MM-YYYY HH:mm:ss") || "--",
      sortable: true,
      width: "160px",
    },
    {
      name: "Remaining Time",
      cell: (row) => (
        <ReverseTimer
          key={row.closingDateTime}
          openingDateTime={row.openingDateTime}
          closingDateTime={row.closingDateTime}
          isClosed={isAuctionExpired(row)}
        />
      ),
      sortable: true,
      width: "270px",
    },
    {
      name: "Live Auction",
      cell: (row) => (
        <button
          className="btn btnTable"
          onClick={() => liveAuction(row)}
          style={{
            background: "#0f766e",
            color: "#fff",
            border: "none",
            fontWeight: 600,
            fontSize: "12px",
            cursor: "pointer",
          }}
        >
          <i className="bi bi-broadcast me-1"></i>
          Live
        </button>
      ),
      selector: (row) => row.liveAuction || "--",
      sortable: true,
      width: "130px",
    },
    {
      name: "Auction Title",
      cell: (row) => (
        <span className="m-0 p-0 pt-1 fw-bold">{row?.auctionTitle}</span>
      ),
      selector: (row) => row.auctionTitle || "--",
      sortable: true,
      width: "160px",
    },
    // {
    //   name: "Auction Description",
    //   cell: (row) => (
    //     <span className="m-0 p-0 pt-1 fw-bold">{row?.auctionDescription}</span>
    //   ),
    //   selector: (row) => row.auctionDescription || "--",
    //   sortable: true,
    //   width: "200px",
    // },
  ];

  return (
    <main className="dashboard main" id="main">
      <Layout />
      <Row className="justify-center subContent">
        <Col xs={12} md={12} className="rightSide">
          <div className="rightSideInner PageCommonCSS">
            <Row>
              <Col xs={12} md={9} lg={9}>
                <h2 className="mainTitle"> Auction</h2>
                <p className="subText">Lists of  Auction and details</p>
              </Col>
              <Col xs={12} md={3} lg={3} className="mt-3">
                <SearchField
                  name="searchKey"
                  value={getCommonSearchTxt}
                  onChange={(event) =>
                    setCommonSearchTxt(event.target.value)
                  }
                  onClick={() => handleSearchFuncApi()}
                />
              </Col>
            </Row>
            <hr />
            <div className="accordion" id="accordionExample">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <Row className="accordion-button">
                    <Col xs={1} md={1} lg={1} className="text-left arrowBtn">
                      <i
                        className={`bi ${
                          isCardVisible
                            ? "bi-chevron-right"
                            : "bi-chevron-down"
                        }`}
                        onClick={toggleCardVisibility}
                      ></i>
                    </Col>
                    <Col xs={1} md={1} lg={1} className="text-center logoBtn">
                      <span className="iconLogo">RL</span>
                    </Col>
                    <Col xs={12} md={8} lg={8}>
                      <span className="headerTitle"> Auction List</span>
                      <span className="subHeaderTitle">
                        View of  Auction and Status
                      </span>
                    </Col>
                    <Col
                      xs={4}
                      md={2}
                      lg={2}
                      className="d-flex justify-content-end"
                    >
                      <button
                        className="btnTable"
                        onClick={detailedCommercialEvaluation}
                      >
                        <i className="bi bi-plus-lg"></i> Create Auction
                      </button>
                    </Col>
                  </Row>
                </h2>

                <div
                  className={`accordion-collapse collapse show ${
                    isCardVisible ? "visible" : ""
                  }`}
                >
                  <div className="accordion-body">
                    <Row>
                      <StickyColumn
                        columnIndexes={[3, 4]}
                        leftOffsets={[0, 140]}
                      />
                      <Col xs={12} md={12} lg={12}>
                        <DataTable
                          title=""
                          columns={columns}
                          data={data}
                          paginationRowsPerPageOptions={Constants.ROW_PER_PAGE}
                          pagination
                          paginationServer
                          progressPending={customerData?.status}
                          paginationTotalRows={customerTable.total}
                          onChangeRowsPerPage={handlePerRowsChange}
                          onChangePage={handlePageChange}
                          selectableRows
                        />
                      </Col>
                    </Row>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <CommonItemLevelDatatableModal
        show={itemLevelModalShow}
        onHide={() => itemLevelHideModal()}
      />

      {chatId && (
        <ChatBox
          groupId={chatId}
          chatTitle="User"
          onClose={() => setChatId(null)}
        />
      )}
    </main>
  );
};

export default BuyerReverseAuctionList;
